import { showToast } from 'components/common/CustomToast';
import TariffItem from 'components/park/tariff/TariffItem';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ParkingZoneType } from 'types/common';
import {
  AccessMethod,
  AccessType,
  CountryCode,
  Maybe,
  SegmentProps,
} from 'types/generatedSchemaTypes';
import { getCurrencyString } from 'utils/commonUtils';
import Logger from 'utils/Logger';

const ParkAndChargeTariffItem = ({
  segment,
  countryCode,
  zoneCode,
  isSelected,
  onPress,
}: {
  segment?: Maybe<SegmentProps>;
  countryCode: CountryCode;
  zoneCode?: string;
  isSelected: boolean;
  onPress: (
    tariffId: string,
    tariffName: string,
    type: ParkingZoneType,
    isUnlockValid?: boolean
  ) => void;
}) => {
  const {
    tariffPlanId,
    notes,
    chargingPriceKwh,
    title,
    accessType,
    accessMethod,
    uid,
  } = segment as SegmentProps;

  const { t } = useTranslation();

  const segmentNote = useMemo(() => {
    return (
      notes?.map((note) => note?.text).join(' ') ||
      `${chargingPriceKwh} ${getCurrencyString(countryCode)} /kWh`
    );
  }, [chargingPriceKwh, countryCode, notes]);

  const handleSetTariff = (
    _tariffId: string,
    _tariffName: string,
    _type: ParkingZoneType,
    _accessType?: AccessType,
    _accessMethod?: AccessMethod,
    isUnlockValid?: boolean,
    _chargingTariff?: SegmentProps
  ) => {
    onPress(uid || '', title || '-', 'CHARGING', isUnlockValid);
  };

  //In some places charging is "Free" (e.g. parking zone SE-3211701), charging price is 0, so we should only catch
  // values that are not numbers or less than 0
  if (typeof chargingPriceKwh !== 'number' || chargingPriceKwh < 0) {
    Logger.error(
      'Invalid charging price',
      `chargingPriceKwh: ${chargingPriceKwh}, tariffPlanId: ${segment?.tariffPlanId}`
    );
    showToast(t('error.invalidChargingPrice'), 'info');
    return <></>;
  }

  return (
    <TariffItem
      isSelected={isSelected}
      key={`tariff-item-${tariffPlanId}`}
      tariffPlan={{
        id: tariffPlanId,
        name: title,
        accessType: accessType,
        accessMethod: accessMethod,
        note: segmentNote,
        uid,
      }}
      zoneCode={zoneCode}
      countryCode={countryCode}
      type="CHARGING"
      onPress={handleSetTariff}
    />
  );
};

export default ParkAndChargeTariffItem;
